/**
 * @file   src\store\slices\reportSlice.ts
 * @brief  This file is responsible for creating report related slices to call actions and state management.
 * @date   Oct, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { createSlice } from '@reduxjs/toolkit';
import { IReports } from '../../interfaces/reportInterface';
import {
  getCaAttendanceReport,
  exportAttendanceReportAsExcel,
  revertAllReportState,
  getCalloutReport,
  exportCalloutReportAsPdf,
  getStaffSummaryReport,
  exportStaffSummaryReportAsPdf,
  getShiftSummaryReport,
  exportShiftSummaryReportAsPdf,
  exportCalloutReportAsExcel,
  exportStaffSummaryReportAsExcel,
  exportShiftSummaryReportAsExcel,
} from '../actions/reportAction';
import { OVERSTAFFED } from '../../utils/constants';

// Initial state.
const reportState: IReports = {
  isFetchAttendanceReportSuccess: false,
  isFetchAttendanceReportLoading: false,
  fetchAttendanceReportErrorCode: 0,
  fetchAttendanceReportMessage: null,
  attendanceData: null,
  totalAttendance: 0,
  isExportAttendanceExcelSuccess: false,
  isExportAttendanceExcelLoading: false,
  exportAttendanceExcelErrorCode: 0,
  exportAttendanceExcelMessage: null,
  attendanceExcelData: null,
  isCalloutReportSuccess: false,
  isCalloutReportLoading: false,
  calloutReportErrorCode: 0,
  calloutReportMessage: null,
  calloutReportData: null,
  totalCallout: 0,
  isExportCalloutPdfSuccess: false,
  isExportCalloutPdfLoading: false,
  exportCalloutPdfErrorCode: 0,
  exportCalloutPdfMessage: null,
  callOutPdfData: null,
  isStaffSummaryReportSuccess: false,
  isStaffSummaryReportLoading: false,
  staffSummaryReportErrorCode: 0,
  staffSummaryReportMessage: null,
  staffSummaryReportData: null,
  totalStaffSummary: 0,
  overstaffCap: 0,
  isExportStaffSummaryPdfSuccess: false,
  isExportStaffSummaryPdfLoading: false,
  exportStaffSummaryPdfErrorCode: 0,
  exportStaffSummaryPdfMessage: null,
  staffSummaryPdfData: null,
  isShiftSummaryReportSuccess: false,
  isShiftSummaryReportLoading: false,
  shiftSummaryReportErrorCode: 0,
  shiftSummaryReportMessage: null,
  shiftSummaryReportData: null,
  totalShiftSummary: 0,
  isExportShiftSummaryPdfSuccess: false,
  isExportShiftSummaryPdfLoading: false,
  exportShiftSummaryPdfErrorCode: 0,
  exportShiftSummaryPdfMessage: null,
  shiftSummaryPdfData: null,
  isExportCalloutExcelSuccess: false,
  isExportCalloutExcelLoading: false,
  exportCalloutExcelErrorCode: 0,
  exportCalloutExcelMessage: null,
  calloutExcelData: null,
  isExportStaffSummaryExcelSuccess: false,
  isExportStaffSummaryExcelLoading: false,
  exportStaffSummaryExcelErrorCode: 0,
  exportStaffSummaryExcelMessage: null,
  staffSummaryExcelData: null,
  isExportShiftSummaryExcelSuccess: false,
  isExportShiftSummaryExcelLoading: false,
  exportShiftSummaryExcelErrorCode: 0,
  exportShiftSummaryExcelMessage: null,
  shiftSummaryExcelData: null,
};

// Config report slice.
export const reportSlice = createSlice({
  name: 'report',
  initialState: reportState,
  reducers: {
    resetReportState: () => reportState,
  },
  extraReducers(builder) {
    // ********************* Start of get all CA attendance report listing action state handling. *********************
    // Get all CA attendance report default state.
    builder.addCase(getCaAttendanceReport.pending, (state) => {
      state.isFetchAttendanceReportSuccess = false;
      state.isFetchAttendanceReportLoading = true;
      state.fetchAttendanceReportErrorCode = 0;
      state.fetchAttendanceReportMessage = '';
    });
    // Get all CA attendance report success state.
    builder.addCase(getCaAttendanceReport.fulfilled, (state, action) => {
      const { status, info } = action.payload;
      const error = action.payload?.error || 0;
      state.isFetchAttendanceReportLoading = false;
      state.isFetchAttendanceReportSuccess = true;
      state.fetchAttendanceReportErrorCode = error;
      state.fetchAttendanceReportMessage = status || '';
      state.attendanceData = info?.attendance || [];
      state.totalAttendance = info?.total || 0;
    });
    // Get all CA attendance report error state.
    builder.addCase(getCaAttendanceReport.rejected, (state, action: any) => {
      const { errcode, error } = action.payload ?? {};
      state.isFetchAttendanceReportSuccess = false;
      state.isFetchAttendanceReportLoading = false;
      state.fetchAttendanceReportErrorCode = errcode;
      state.fetchAttendanceReportMessage = error || '';
    });
    // ********************* End of get all CA attendance report listing action state handling. *********************

    // ********************* Start of export CA attendance report as excel action state handling. *********************
    // Export CA attendance report as excel default state.
    builder.addCase(exportAttendanceReportAsExcel.pending, (state) => {
      state.isExportAttendanceExcelSuccess = false;
      state.isExportAttendanceExcelLoading = true;
      state.exportAttendanceExcelErrorCode = 0;
      state.exportAttendanceExcelMessage = '';
    });
    // Export CA attendance report as excel success state.
    builder.addCase(exportAttendanceReportAsExcel.fulfilled, (state, action) => {
      state.isExportAttendanceExcelLoading = false;
      state.isExportAttendanceExcelSuccess = true;
      state.exportAttendanceExcelErrorCode = 0;
      state.attendanceExcelData = action.payload;
      state.exportAttendanceExcelMessage = null;
    });
    // Export CA attendance report as excel error state.
    builder.addCase(exportAttendanceReportAsExcel.rejected, (state, action: any) => {
      const { errcode, error } = action.payload?.error || 0;
      state.isExportAttendanceExcelSuccess = false;
      state.isExportAttendanceExcelLoading = false;
      state.exportAttendanceExcelErrorCode = errcode;
      state.exportAttendanceExcelMessage = error || '';
    });
    // ********************* End of export CA attendance report as excel action state handling. *********************

    // ********************* Start of get all callout report listing action state handling. *********************
    // Get callout report default state.
    builder.addCase(getCalloutReport.pending, (state) => {
      state.isCalloutReportSuccess = false;
      state.isCalloutReportLoading = true;
      state.calloutReportErrorCode = 0;
      state.calloutReportMessage = '';
    });
    // Get callout report success state.
    builder.addCase(getCalloutReport.fulfilled, (state, action) => {
      const { status, info } = action.payload;
      const error = action.payload?.error || 0;
      state.isCalloutReportLoading = false;
      state.isCalloutReportSuccess = true;
      state.calloutReportErrorCode = error;
      state.calloutReportMessage = status || '';
      state.calloutReportData = info?.Data || [];
      state.totalCallout = info?.total || 0;
    });
    // Get callout report error state.
    builder.addCase(getCalloutReport.rejected, (state, action: any) => {
      const { errcode, error } = action.payload ?? {};
      state.isCalloutReportSuccess = false;
      state.isCalloutReportLoading = false;
      state.calloutReportErrorCode = errcode;
      state.calloutReportMessage = error || '';
    });
    // ********************* End of get callout report listing action state handling. *********************

    // ********************* Start of export callout report as pdf action state handling. *********************
    // Export callout report as pdf default state.
    builder.addCase(exportCalloutReportAsPdf.pending, (state) => {
      state.isExportCalloutPdfSuccess = false;
      state.isExportCalloutPdfLoading = true;
      state.exportCalloutPdfErrorCode = 0;
      state.exportCalloutPdfMessage = '';
    });
    // Export callout report as pdf success state.
    builder.addCase(exportCalloutReportAsPdf.fulfilled, (state, action) => {
      state.isExportCalloutPdfLoading = false;
      state.isExportCalloutPdfSuccess = true;
      state.exportCalloutPdfErrorCode = 0;
      state.callOutPdfData = action.payload;
      state.exportCalloutPdfMessage = null;
    });
    // Export callout report as pdf error state.
    builder.addCase(exportCalloutReportAsPdf.rejected, (state, action: any) => {
      const { errcode, error } = action.payload?.error || 0;
      state.isExportCalloutPdfSuccess = false;
      state.isExportCalloutPdfLoading = false;
      state.exportCalloutPdfErrorCode = errcode;
      state.exportCalloutPdfMessage = error || '';
    });
    // ********************* End of export callout report as pdf action state handling. *********************

    // ********************* Start of get all staff summary report listing action state handling. *********************
    // Get staff summary report default state.
    builder.addCase(getStaffSummaryReport.pending, (state) => {
      state.isStaffSummaryReportSuccess = false;
      state.isStaffSummaryReportLoading = true;
      state.staffSummaryReportErrorCode = 0;
      state.staffSummaryReportMessage = '';
    });
    // Get staff summary report success state.
    builder.addCase(getStaffSummaryReport.fulfilled, (state, action) => {
      const { status, info } = action.payload;
      const error = action.payload?.error || 0;
      state.isStaffSummaryReportLoading = false;
      state.isStaffSummaryReportSuccess = true;
      state.staffSummaryReportErrorCode = error;
      state.staffSummaryReportMessage = status || '';
      state.staffSummaryReportData = info?.Data || [];
      state.totalStaffSummary = info?.total || 0;
      state.overstaffCap = info?.overstaffCap || OVERSTAFFED;
    });
    // Get staff summary report error state.
    builder.addCase(getStaffSummaryReport.rejected, (state, action: any) => {
      const { errcode, error } = action.payload ?? {};
      state.isStaffSummaryReportSuccess = false;
      state.isStaffSummaryReportLoading = false;
      state.staffSummaryReportErrorCode = errcode;
      state.staffSummaryReportMessage = error || '';
    });
    // ********************* End of get staff summary report listing action state handling. *********************

    // ********************* Start of export staff summary report as pdf action state handling. *********************
    // Export staff summary report as pdf default state.
    builder.addCase(exportStaffSummaryReportAsPdf.pending, (state) => {
      state.isExportStaffSummaryPdfSuccess = false;
      state.isExportStaffSummaryPdfLoading = true;
      state.exportStaffSummaryPdfErrorCode = 0;
      state.exportStaffSummaryPdfMessage = '';
    });
    // Export staff summary report as pdf success state.
    builder.addCase(exportStaffSummaryReportAsPdf.fulfilled, (state, action) => {
      state.isExportStaffSummaryPdfLoading = false;
      state.isExportStaffSummaryPdfSuccess = true;
      state.exportStaffSummaryPdfErrorCode = 0;
      state.staffSummaryPdfData = action.payload;
      state.exportStaffSummaryPdfMessage = null;
    });
    // Export staff summary report as pdf error state.
    builder.addCase(exportStaffSummaryReportAsPdf.rejected, (state, action: any) => {
      const { errcode, error } = action.payload?.error || 0;
      state.isExportStaffSummaryPdfSuccess = false;
      state.isExportStaffSummaryPdfLoading = false;
      state.exportStaffSummaryPdfErrorCode = errcode;
      state.exportStaffSummaryPdfMessage = error || '';
    });
    // ********************* End of export staff summary report as pdf action state handling. *********************

    // ********************* Start of get all shift summary report listing action state handling. *********************
    // Get shift summary report default state.
    builder.addCase(getShiftSummaryReport.pending, (state) => {
      state.isShiftSummaryReportSuccess = false;
      state.isShiftSummaryReportLoading = true;
      state.shiftSummaryReportErrorCode = 0;
      state.shiftSummaryReportMessage = '';
    });
    // Get shift summary report success state.
    builder.addCase(getShiftSummaryReport.fulfilled, (state, action) => {
      const { status, info } = action.payload;
      const error = action.payload?.error || 0;
      state.isShiftSummaryReportLoading = false;
      state.isShiftSummaryReportSuccess = true;
      state.shiftSummaryReportErrorCode = error;
      state.shiftSummaryReportMessage = status || '';
      state.shiftSummaryReportData = info?.Data || null;
      state.totalShiftSummary = info?.total || 0;
    });
    // Get shift summary report error state.
    builder.addCase(getShiftSummaryReport.rejected, (state, action: any) => {
      const { errcode, error } = action.payload ?? {};
      state.isShiftSummaryReportSuccess = false;
      state.isShiftSummaryReportLoading = false;
      state.shiftSummaryReportErrorCode = errcode;
      state.shiftSummaryReportMessage = error || '';
    });
    // ********************* End of get shift summary report listing action state handling. *********************

    // ********************* Start of export shift summary report as pdf action state handling. *********************
    // Export shift summary report as pdf default state.
    builder.addCase(exportShiftSummaryReportAsPdf.pending, (state) => {
      state.isExportShiftSummaryPdfSuccess = false;
      state.isExportShiftSummaryPdfLoading = true;
      state.exportShiftSummaryPdfErrorCode = 0;
      state.exportShiftSummaryPdfMessage = '';
    });
    // Export shift summary report as pdf success state.
    builder.addCase(exportShiftSummaryReportAsPdf.fulfilled, (state, action) => {
      state.isExportShiftSummaryPdfLoading = false;
      state.isExportShiftSummaryPdfSuccess = true;
      state.exportShiftSummaryPdfErrorCode = 0;
      state.shiftSummaryPdfData = action.payload;
      state.exportShiftSummaryPdfMessage = null;
    });
    // Export shift summary report as pdf error state.
    builder.addCase(exportShiftSummaryReportAsPdf.rejected, (state, action: any) => {
      const { errcode, error } = action.payload?.error || 0;
      state.isExportShiftSummaryPdfSuccess = false;
      state.isExportShiftSummaryPdfLoading = false;
      state.exportShiftSummaryPdfErrorCode = errcode;
      state.exportShiftSummaryPdfMessage = error || '';
    });
    // ********************* End of export shift summary report as pdf action state handling. *********************

    // ********************* Start of export callout report as excel action state handling. *********************
    // Export callout report as excel default state.
    builder.addCase(exportCalloutReportAsExcel.pending, (state) => {
      state.isExportCalloutExcelSuccess = false;
      state.isExportCalloutExcelLoading = true;
      state.exportCalloutExcelErrorCode = 0;
      state.exportCalloutExcelMessage = '';
    });
    // Export callout report as excel success state.
    builder.addCase(exportCalloutReportAsExcel.fulfilled, (state, action) => {
      state.isExportCalloutExcelLoading = false;
      state.isExportCalloutExcelSuccess = true;
      state.exportCalloutExcelErrorCode = 0;
      state.calloutExcelData = action.payload;
      state.exportCalloutExcelMessage = null;
    });
    // Export callout report as excel error state.
    builder.addCase(exportCalloutReportAsExcel.rejected, (state, action: any) => {
      const { errcode, error } = action.payload?.error || 0;
      state.isExportCalloutExcelSuccess = false;
      state.isExportCalloutExcelLoading = false;
      state.exportCalloutExcelErrorCode = errcode;
      state.exportCalloutExcelMessage = error || '';
    });
    // ********************* End of export callout report as excel action state handling. *********************

    // ********************* Start of export staff summary report as excel action state handling. *********************
    // Export staff summary report as excel default state.
    builder.addCase(exportStaffSummaryReportAsExcel.pending, (state) => {
      state.isExportStaffSummaryExcelSuccess = false;
      state.isExportStaffSummaryExcelLoading = true;
      state.exportStaffSummaryExcelErrorCode = 0;
      state.exportStaffSummaryExcelMessage = '';
    });
    // Export staff summary report as excel success state.
    builder.addCase(exportStaffSummaryReportAsExcel.fulfilled, (state, action) => {
      state.isExportStaffSummaryExcelLoading = false;
      state.isExportStaffSummaryExcelSuccess = true;
      state.exportStaffSummaryExcelErrorCode = 0;
      state.staffSummaryExcelData = action.payload;
      state.exportStaffSummaryExcelMessage = null;
    });
    // Export staff summary report as excel error state.
    builder.addCase(exportStaffSummaryReportAsExcel.rejected, (state, action: any) => {
      const { errcode, error } = action.payload?.error || 0;
      state.isExportStaffSummaryExcelSuccess = false;
      state.isExportStaffSummaryExcelLoading = false;
      state.exportStaffSummaryExcelErrorCode = errcode;
      state.exportStaffSummaryExcelMessage = error || '';
    });
    // ********************* End of export staff summary report as excel action state handling. *********************

    // ********************* Start of export shift summary report as excel action state handling. *********************
    // Export shift summary report as excel default state.
    builder.addCase(exportShiftSummaryReportAsExcel.pending, (state) => {
      state.isExportShiftSummaryExcelSuccess = false;
      state.isExportShiftSummaryExcelLoading = true;
      state.exportShiftSummaryExcelErrorCode = 0;
      state.exportShiftSummaryExcelMessage = '';
    });
    // Export shift summary report as excel success state.
    builder.addCase(exportShiftSummaryReportAsExcel.fulfilled, (state, action) => {
      state.isExportShiftSummaryExcelLoading = false;
      state.isExportShiftSummaryExcelSuccess = true;
      state.exportShiftSummaryExcelErrorCode = 0;
      state.shiftSummaryExcelData = action.payload;
      state.exportShiftSummaryExcelMessage = null;
    });
    // Export shift summary report as excel error state.
    builder.addCase(exportShiftSummaryReportAsExcel.rejected, (state, action: any) => {
      const { errcode, error } = action.payload?.error || 0;
      state.isExportShiftSummaryExcelSuccess = false;
      state.isExportShiftSummaryExcelLoading = false;
      state.exportShiftSummaryExcelErrorCode = errcode;
      state.exportShiftSummaryExcelMessage = error || '';
    });
    // ********************* End of export shift summary report as excel action state handling. *********************

    // ********************* Clear all report states to initial state action. *********************
    builder.addCase(revertAllReportState, (state) => {
      state.isFetchAttendanceReportSuccess = false;
      state.isFetchAttendanceReportLoading = false;
      state.fetchAttendanceReportErrorCode = 0;
      state.fetchAttendanceReportMessage = null;
      state.isExportAttendanceExcelSuccess = false;
      state.isExportAttendanceExcelLoading = false;
      state.exportAttendanceExcelErrorCode = 0;
      state.exportAttendanceExcelMessage = null;
      state.isCalloutReportSuccess = false;
      state.isCalloutReportLoading = false;
      state.calloutReportErrorCode = 0;
      state.calloutReportMessage = null;
      state.isExportCalloutPdfSuccess = false;
      state.isExportCalloutPdfLoading = false;
      state.exportCalloutPdfErrorCode = 0;
      state.exportCalloutPdfMessage = null;
      state.isStaffSummaryReportSuccess = false;
      state.isStaffSummaryReportLoading = false;
      state.staffSummaryReportErrorCode = 0;
      state.staffSummaryReportMessage = null;
      state.isExportStaffSummaryPdfSuccess = false;
      state.isExportStaffSummaryPdfLoading = false;
      state.exportStaffSummaryPdfErrorCode = 0;
      state.exportStaffSummaryPdfMessage = null;
      state.isShiftSummaryReportSuccess = false;
      state.isShiftSummaryReportLoading = false;
      state.shiftSummaryReportErrorCode = 0;
      state.shiftSummaryReportMessage = null;
      state.isExportShiftSummaryPdfSuccess = false;
      state.isExportShiftSummaryPdfLoading = false;
      state.exportShiftSummaryPdfErrorCode = 0;
      state.exportShiftSummaryPdfMessage = null;
      state.isExportCalloutExcelSuccess = false;
      state.isExportCalloutExcelLoading = false;
      state.exportCalloutExcelErrorCode = 0;
      state.exportCalloutExcelMessage = null;
      state.isExportStaffSummaryExcelSuccess = false;
      state.isExportStaffSummaryExcelLoading = false;
      state.exportStaffSummaryExcelErrorCode = 0;
      state.exportStaffSummaryExcelMessage = null;
      state.isExportShiftSummaryExcelSuccess = false;
      state.isExportShiftSummaryExcelLoading = false;
      state.exportShiftSummaryExcelErrorCode = 0;
      state.exportShiftSummaryExcelMessage = null;
    });
  },
});
// Export report actions.
export const { resetReportState } = reportSlice.actions;
// Export reducer.
export default reportSlice.reducer;
